<template>
  <div class="calllog">
    <div class="calllog__content">
      <call-log-item
        v-for="log in uniqueLogs"
        :key="log.id"
        :log="log"
      />
      <v-skeleton-loader
        v-if="loading"
        class="w-full"
        color="var(--color-base-50)"
        type="image"
      />
      <div
        :key="meta.current_page"
        v-observe-visibility="loadMore"
      />
    </div>
  </div>
</template>

<script>
  import { useSipStore } from '@bv/stores';
  import { mapState } from 'pinia';

  import CallLogsApi from '@/api/webphone/callLogs.api';
  import { uniqueArray } from '@/utilities';

  import CallLogItem from './CallLogItem.vue';

  export default {
    name: 'CallLogList',
    components: {
      CallLogItem,
    },
    props: {
      activeTab: {
        type: String,
        default: 'all',
      },
      search: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        logs: [],
        loading: false,
        meta: {
          current_page: 0,
        },
      };
    },
    computed: {
      ...mapState(useSipStore, ['recentlyAddedLog']),
      changeWatcher() {
        return {
          activeTab: this.activeTab,
          search: this.search,
        };
      },
      uniqueLogs() {
        // this filter is important for no duplication in logs in case log is added after fetch
        return uniqueArray(this.logs, 'phone_number');
      },
    },
    watch: {
      changeWatcher: {
        handler() {
          this.logs = [];
          this.meta = { current_page: 0 };
          this.loadMore();
        },
        deep: true,
      },
      recentlyAddedLog: {
        handler(log) {
          if (
            log &&
            log.phone_number.includes(this.search) &&
            (this.activeTab === 'missed' ? log.status === this.activeTab : true) &&
            (['outbound', 'inbound'].includes(this.activeTab) ? log.direction === this.activeTab : true)
          ) {
            const changedLogIndex = this.logs.findIndex((l) => l.phone_number === log?.phone_number);

            if (changedLogIndex >= 0) {
              this.logs.splice(changedLogIndex, 1);
            }

            this.logs.unshift(log);
          }
        },
        deep: true,
      },
    },
    methods: {
      loadMore(isVisible = true) {
        if (!isVisible || this.meta.current_page >= this.meta.last_page || this.loading) {
          return;
        }

        this.fetchLogsList({
          page: this.meta.current_page + 1,
          'filter[q]': this.search,
          'filter[status]': this.activeTab === 'missed' ? this.activeTab : undefined,
          'filter[direction]': ['outbound', 'inbound'].includes(this.activeTab) ? this.activeTab : undefined,
        });
      },
      fetchLogsList(filter) {
        this.loading = true;

        CallLogsApi.get({ params: filter }, 'logs-overview')
          .then(({ data }) => {
            if (filter?.page && filter.page > 1) {
              this.logs.push(...data.data);
            } else {
              this.logs = data.data;
            }

            this.meta = data.meta;
          })
          .finally(() => {
            this.loading = false;
          });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .calllog {
    width: 100%;
    height: calc(100% - 56px - 16px);
    overflow: hidden;
    flex-grow: 1;

    &__content {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 100%;
      height: 100%;
      overflow-y: auto;
      padding-inline-end: 4px;
    }
  }
</style>
